import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Code,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FiEdit, FiPlus, FiSave, FiTrash2 } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RuntimeConfigJsonEditor } from './RuntimeConfigJsonEditor'
import { getRuntimeJsonDescriptor } from './runtimeConfigJsonDescriptors'
import {
  formatJsonEditorValue,
  serializeJsonEditorValue,
  type JsonValue,
  type JsonStorageKind,
} from './runtimeConfigJsonEditorUtils'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment, type NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type { RuntimeConfigUpdateResult } from '@/services/admin/systemService'
import {
  useRefreshRuntimeConfigCache,
  useSystemChains,
  useRuntimeConfigCurrent,
  useUpdateRuntimeConfigCurrent,
} from '@/hooks/useSystemConfigData'
import { resolveRuntimeConfigModuleId } from './runtimeConfigModules'

type RuntimeConfigTabProps = {
  cardBg: string
}

type RuntimeConfigOverrides = {
  default: unknown
  overrides?: Record<string, unknown>
}

type ValueKind = 'boolean' | 'number' | 'string' | 'json-string' | 'object' | 'unknown'

type RuntimeConfigEntry = {
  key: string
  label: string
  moduleId: string
  moduleLabel: string
  rawValue: unknown
  defaultValue: unknown
  resolvedValue: unknown
  overrides: Record<string, unknown>
  valueKind: ValueKind
}

const SENSITIVE_KEY_PATTERNS = [
  /SECRET/i,
  /PRIVATE_KEY/i,
  /SIGNING_KEY/i,
  /PASSWORD/i,
  /API_KEY/i,
  /ACCESS_KEY/i,
  /CLIENT_SECRET/i,
  /AUTH_TOKEN/i,
  /BEARER_TOKEN/i,
]

const MODULE_ORDER = [
  'admin',
  'feature',
  'cache',
  'rateLimit',
  'auth',
  'security',
  'mail',
  'support',
  'webhook',
  'alchemy',
  'onchain',
  'gas',
  'paymentConsistency',
  'payment',
  'order',
  'platform',
  'testnet',
  'turnstile',
  'other',
]

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? {}, null, 2)
  } catch {
    return '{}'
  }
}

const isOverrideShape = (value: unknown): value is RuntimeConfigOverrides => {
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.prototype.hasOwnProperty.call(value, 'default')
  )
}

const resolveRuntimeConfigValue = (raw: unknown, envKey: string): unknown => {
  if (!isOverrideShape(raw)) {
    return raw
  }
  const overrides = raw.overrides ?? {}
  if (envKey && Object.prototype.hasOwnProperty.call(overrides, envKey)) {
    return overrides[envKey]
  }
  return raw.default
}

const getDefaultValue = (raw: unknown): unknown => {
  if (!isOverrideShape(raw)) {
    return raw
  }
  return raw.default
}

const getOverrides = (raw: unknown): Record<string, unknown> => {
  if (!isOverrideShape(raw)) {
    return {}
  }
  return raw.overrides ?? {}
}

const detectValueKind = (value: unknown): ValueKind => {
  if (value === null || value === undefined) {
    return 'string'
  }
  if (typeof value === 'boolean') {
    return 'boolean'
  }
  if (typeof value === 'number') {
    return 'number'
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(value) as unknown
        if (typeof parsed === 'object' && parsed !== null) {
          return 'json-string'
        }
      } catch {
        return 'string'
      }
    }
    return 'string'
  }
  if (typeof value === 'object') {
    return 'object'
  }
  return 'unknown'
}

const formatKeyLabel = (key: string): string => {
  return key
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(part => part[0]?.toUpperCase() + part.slice(1))
    .join(' ')
}

const isSensitiveRuntimeKey = (key: string): boolean => {
  return SENSITIVE_KEY_PATTERNS.some(pattern => pattern.test(key))
}

const formatValueForInput = (value: unknown, kind: ValueKind): string => {
  if (value === undefined || value === null) {
    return ''
  }
  if (kind === 'object') {
    return JSON.stringify(value, null, 2)
  }
  if (kind === 'json-string') {
    try {
      const parsed = JSON.parse(String(value))
      return JSON.stringify(parsed, null, 2)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

const parseInputValue = (input: string, kind: ValueKind): unknown => {
  if (kind === 'boolean') {
    return input.toLowerCase() === 'true'
  }
  if (kind === 'number') {
    const parsed = Number(input)
    if (!Number.isFinite(parsed)) {
      throw new Error('Invalid number')
    }
    return parsed
  }
  if (kind === 'object') {
    return JSON.parse(input)
  }
  if (kind === 'json-string') {
    const parsed = JSON.parse(input)
    return JSON.stringify(parsed)
  }
  return input
}

const truncateText = (value: string, limit = 120): string => {
  if (value.length <= limit) {
    return value
  }
  return `${value.slice(0, limit)}…`
}

const formatOverrideValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-'
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(value) as unknown
        if (parsed && typeof parsed === 'object') {
          const count = Array.isArray(parsed)
            ? parsed.length
            : Object.keys(parsed as Record<string, unknown>).length
          return `JSON(${count})`
        }
      } catch {
        return truncateText(value, 60)
      }
    }
    return truncateText(value, 60)
  }
  if (typeof value === 'object') {
    try {
      const count = Array.isArray(value)
        ? value.length
        : Object.keys(value as Record<string, unknown>).length
      return `JSON(${count})`
    } catch {
      return truncateText(JSON.stringify(value), 60)
    }
  }
  return truncateText(String(value), 60)
}

const formatOverrideValueFull = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(value) as unknown
        return JSON.stringify(parsed, null, 2)
      } catch {
        return value
      }
    }
    return value
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

export function RuntimeConfigTab(props: RuntimeConfigTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const { data: item, isFetching, refetch } = useRuntimeConfigCurrent()
  const { data: chains } = useSystemChains()
  const updateMutation = useUpdateRuntimeConfigCurrent()
  const refreshMutation = useRefreshRuntimeConfigCache()
  const [editorValue, setEditorValue] = useState('{}')
  const [viewMode, setViewMode] = useState<'table' | 'modules'>('table')
  const [searchText, setSearchText] = useState('')
  const [editingEntry, setEditingEntry] = useState<RuntimeConfigEntry | null>(null)
  const [editDefaultValue, setEditDefaultValue] = useState('')
  const [editOverrides, setEditOverrides] = useState<Array<{
    id: string
    env: string
    value: string
    jsonValue?: JsonValue
  }>>([])
  const [editKind, setEditKind] = useState<ValueKind>('string')
  const [editJsonDefaultValue, setEditJsonDefaultValue] = useState<JsonValue | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const environmentKey = (
    import.meta.env.VITE_ENVIRONMENT
    ?? import.meta.env.VITE_APP_ENV
    ?? 'production'
  ).toLowerCase()
  const booleanSelectOptions = useMemo(
    () => [
      { value: 'true', label: t('common.enabled', { defaultValue: 'Enabled' }) },
      { value: 'false', label: t('common.disabled', { defaultValue: 'Disabled' }) },
    ],
    [t]
  )

  const itemVersion = item?.version ?? 'v1'
  const itemUpdatedAt = item?.updatedAt ?? '-'
  const itemEnv: NetworkEnvironment | null = item
    ? item.isTestnet
      ? 'testnet'
      : 'mainnet'
    : null
  const isItemEnvMismatch = Boolean(itemEnv && itemEnv !== currentEnv)
  const isDataHydrating = isFetching || !item || isItemEnvMismatch

  useEffect(() => {
    if (!item) {
      return
    }
    const sanitizedConfig = Object.fromEntries(
      Object.entries(item.configJson).filter(([key]) => !isSensitiveRuntimeKey(key))
    )
    setEditorValue(prettyJson(sanitizedConfig))
  }, [item?.id, item?.updatedAt, item])

  useEffect(() => {
    setEditingEntry(null)
    setShowAdvanced(false)
  }, [currentEnv])

  useEffect(() => {
    if (!editingEntry) {
      return
    }
    const raw = editingEntry.rawValue
    const overrides = getOverrides(raw)
    const defaultValue = getDefaultValue(raw)
    const valueKind = detectValueKind(defaultValue)
    const descriptor = getRuntimeJsonDescriptor(editingEntry.key)
    setEditKind(valueKind)
    setEditDefaultValue(formatValueForInput(defaultValue, valueKind))
    if (valueKind === 'object' || valueKind === 'json-string') {
      try {
        const storageKind: JsonStorageKind = valueKind === 'json-string'
          ? 'json-string'
          : descriptor.storageKind
        setEditJsonDefaultValue(formatJsonEditorValue(defaultValue, storageKind))
      } catch {
        setEditJsonDefaultValue(null)
      }
    } else {
      setEditJsonDefaultValue(null)
    }
    setEditOverrides(
      Object.entries(overrides).map(([env, value], index) => ({
        id: `${env}-${index}`,
        env,
        value: formatOverrideValueFull(value),
        ...(valueKind === 'object' || valueKind === 'json-string'
          ? {
              jsonValue: (() => {
                try {
                  const storageKind: JsonStorageKind = valueKind === 'json-string'
                    ? 'json-string'
                    : descriptor.storageKind
                  return formatJsonEditorValue(value, storageKind)
                } catch {
                  return undefined
                }
              })(),
            }
          : {}),
      }))
    )
  }, [editingEntry, environmentKey])

  const infoText = useMemo(() => {
    if (!item) {
      return t('common.noData', { defaultValue: 'No data' })
    }
    if (isItemEnvMismatch && itemEnv) {
      return t('systemConfig.runtimeConfig.networkMismatch', {
        defaultValue: 'Reloading {{currentEnv}} data (current payload: {{itemEnv}})',
        currentEnv,
        itemEnv,
      })
    }
    return `version=${itemVersion}, updatedAt=${itemUpdatedAt}`
  }, [currentEnv, isItemEnvMismatch, item, itemEnv, itemUpdatedAt, itemVersion, t])

  const ensureFreshEnvContext = () => {
    if (!item) {
      toast.warning({
        title: t('systemConfig.runtimeConfig.loadingData', {
          defaultValue: 'Runtime config is still loading',
        }),
      })
      return false
    }

    if (isItemEnvMismatch) {
      toast.warning({
        title: t('systemConfig.runtimeConfig.envChanged', {
          defaultValue: 'Network changed, please wait for latest config to load',
        }),
      })
      void refetch()
      return false
    }

    return true
  }

  const moduleLabel = (moduleId: string) => {
    return t(`systemConfig.runtimeConfig.modules.${moduleId}`, {
      defaultValue: formatKeyLabel(moduleId),
    })
  }

  const chainNameByCode = useMemo(() => {
    const map = new Map<string, string>()
    chains?.forEach(chain => {
      map.set(chain.chainCode, chain.name)
    })
    return map
  }, [chains])

  const entries = useMemo<RuntimeConfigEntry[]>(() => {
    if (!item?.configJson) {
      return []
    }
    return Object.entries(item.configJson).map(([key, raw]) => {
      const moduleId = resolveRuntimeConfigModuleId(key)
      const label = t(`systemConfig.runtimeConfig.labels.${key}`, {
        defaultValue: formatKeyLabel(key),
      })
      return {
        key,
        label,
        moduleId,
        moduleLabel: moduleLabel(moduleId),
        rawValue: raw,
        defaultValue: getDefaultValue(raw),
        resolvedValue: resolveRuntimeConfigValue(raw, environmentKey),
        overrides: getOverrides(raw),
        valueKind: detectValueKind(getDefaultValue(raw)),
      }
    }).filter(entry => !isSensitiveRuntimeKey(entry.key))
  }, [environmentKey, item?.configJson, t])

  const sensitiveEntryCount = useMemo(() => {
    if (!item?.configJson) {
      return 0
    }
    return Object.keys(item.configJson).filter(isSensitiveRuntimeKey).length
  }, [item?.configJson])

  const filteredEntries = useMemo(() => {
    if (!searchText.trim()) {
      return entries
    }
    const needle = searchText.trim().toLowerCase()
    return entries.filter(entry => (
      entry.key.toLowerCase().includes(needle)
      || entry.label.toLowerCase().includes(needle)
      || entry.moduleLabel.toLowerCase().includes(needle)
    ))
  }, [entries, searchText])

  const groupedEntries = useMemo(() => {
    const grouped = new Map<string, RuntimeConfigEntry[]>()
    filteredEntries.forEach(entry => {
      const existing = grouped.get(entry.moduleId) ?? []
      existing.push(entry)
      grouped.set(entry.moduleId, existing)
    })
    return grouped
  }, [filteredEntries])

  const sortedModuleIds = useMemo(() => {
    const ids = Array.from(groupedEntries.keys())
    return ids.sort((a, b) => {
      const aIndex = MODULE_ORDER.indexOf(a)
      const bIndex = MODULE_ORDER.indexOf(b)
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1
      return a.localeCompare(b)
    })
  }, [groupedEntries])


  const getEntryDescription = useCallback((key: string) => {
    switch (key) {
      case 'PAYMENT_STATE_CONSISTENCY_PROFILE':
      case 'WEBHOOK_SOURCE_EVENT_IDEMPOTENCY_ENABLED':
      case 'ONCHAIN_PAYMENT_SIDEFX_JOB_EXECUTION_ENABLED':
      case 'ORDER_STATE_TRANSITION_COORDINATOR_ENABLED':
      case 'ORDER_SIDE_EFFECT_JOBS_ENABLED':
      case 'PAYMENT_LOG_RECONCILIATION_ENABLED':
        return t(`systemConfig.runtimeConfig.descriptions.${key}`)
      case 'SOLANA_PAYMENT_ADDRESS_PROVISIONING_TIMEOUT_MS':
        return t('systemConfig.runtimeConfig.solanaProvisioning.timeout', {
          defaultValue: 'Single synchronous provisioning timeout in milliseconds. Default: 60000.',
        })
      case 'SOLANA_PAYMENT_ADDRESS_PROVISIONING_MAX_RETRIES':
        return t('systemConfig.runtimeConfig.solanaProvisioning.maxRetries', {
          defaultValue: 'Automatic retry count after the first failed attempt. Default: 2.',
        })
      case 'SOLANA_PAYMENT_ADDRESS_PROVISIONING_RETRY_BACKOFF_MS':
        return t('systemConfig.runtimeConfig.solanaProvisioning.retryBackoff', {
          defaultValue: 'Base retry backoff in milliseconds; each retry multiplies by attempt index. Default: 3000.',
        })
      default:
        return null
    }
  }, [t])

  const showRuntimeConfigSaveOutcome = (result: RuntimeConfigUpdateResult) => {
    if (result.cacheDegraded || result.auditDegraded) {
      const degradedSteps = [
        result.cacheDegraded
          ? t('systemConfig.runtimeConfig.cacheRefreshDegraded', {
              defaultValue: 'cache refresh',
            })
          : null,
        result.auditDegraded
          ? t('systemConfig.runtimeConfig.auditWriteDegraded', {
              defaultValue: 'audit logging',
            })
          : null,
      ].filter((step): step is string => Boolean(step))
      toast.warning({
        title: t('systemConfig.runtimeConfig.saveCommittedDegraded', {
          defaultValue: 'Saved; follow-up action degraded',
        }),
        description: degradedSteps.join(', '),
      })
      return
    }
    toast.success({
      title: t('common.saveSuccess', { defaultValue: 'Saved' }),
    })
  }


  const handleSave = async () => {
    if (!item || !ensureFreshEnvContext()) {
      return
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(editorValue)
    } catch {
      toast.error({
        title: t('systemConfig.runtimeConfig.invalidJson', { defaultValue: 'Invalid JSON format' }),
      })
      return
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      toast.error({
        title: t('systemConfig.runtimeConfig.invalidObject', { defaultValue: 'JSON must be an object' }),
      })
      return
    }

    const parsedConfig = parsed as Record<string, unknown>
    if (Object.keys(parsedConfig).some(isSensitiveRuntimeKey)) {
      toast.error({
        title: t('systemConfig.runtimeConfig.containsSensitiveKeys', {
          defaultValue: 'Sensitive runtime keys cannot be edited in Admin Portal',
        }),
      })
      return
    }

    try {
      const result = await updateMutation.mutateAsync({
        configJson: parsedConfig,
        expectedUpdatedAt: item.updatedAt,
      })
      showRuntimeConfigSaveOutcome(result)
      await refetch()
    } catch (error) {
      toast.error({
        title: t('common.saveFailed', { defaultValue: 'Save failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleSaveEntry = async () => {
    if (!editingEntry || !item?.configJson) {
      return
    }
    if (!ensureFreshEnvContext()) {
      return
    }
    try {
      const descriptor = getRuntimeJsonDescriptor(editingEntry.key)
      const isJsonKind = editKind === 'object' || editKind === 'json-string'
      const storageKind: JsonStorageKind = editKind === 'json-string'
        ? 'json-string'
        : descriptor.storageKind
      if (isJsonKind && !editJsonDefaultValue) {
        throw new Error('JSON default value could not be parsed')
      }
      const defaultValue = isJsonKind
        ? serializeJsonEditorValue(editJsonDefaultValue ?? {}, storageKind)
        : parseInputValue(editDefaultValue, editKind)
      const updatedConfig = {
        ...item.configJson,
      } as Record<string, unknown>

      const currentRaw = updatedConfig[editingEntry.key]
      const nextOverrides = editOverrides.reduce<Record<string, unknown>>((acc, item) => {
        const envKey = item.env.trim().toLowerCase()
        if (!envKey) {
          return acc
        }
        if (editKind === 'number' && item.value.trim() === '') {
          throw new Error('Override value cannot be empty')
        }
        if (isJsonKind && !item.jsonValue) {
          throw new Error(`JSON override for ${envKey} could not be parsed`)
        }
        acc[envKey] = isJsonKind
          ? serializeJsonEditorValue(item.jsonValue ?? {}, storageKind)
          : parseInputValue(item.value, editKind)
        return acc
      }, {})

      if (isOverrideShape(currentRaw) || Object.keys(nextOverrides).length > 0) {
        const nextValue: RuntimeConfigOverrides = {
          ...(isOverrideShape(currentRaw) ? currentRaw : {}),
          default: defaultValue,
        }
        if (Object.keys(nextOverrides).length > 0) {
          nextValue.overrides = nextOverrides
        } else {
          delete nextValue.overrides
        }
        updatedConfig[editingEntry.key] = nextValue
      } else {
        updatedConfig[editingEntry.key] = defaultValue
      }

      const result = await updateMutation.mutateAsync({
        configJson: updatedConfig,
        expectedUpdatedAt: item.updatedAt,
      })
      showRuntimeConfigSaveOutcome(result)
      setEditingEntry(null)
    } catch (error) {
      toast.error({
        title: t('systemConfig.runtimeConfig.invalidValue', {
          defaultValue: 'Invalid value',
        }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleAddOverride = () => {
    setEditOverrides(prev => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, env: '', value: '' },
    ])
  }

  const handleUpdateOverrideEnv = (id: string, env: string) => {
    setEditOverrides(prev => prev.map(item => (item.id === id ? { ...item, env } : item)))
  }

  const handleUpdateOverrideValue = (id: string, value: string) => {
    setEditOverrides(prev => prev.map(item => (item.id === id ? { ...item, value } : item)))
  }

  const handleUpdateOverrideJsonValue = (id: string, jsonValue: JsonValue) => {
    setEditOverrides(prev => prev.map(item => (item.id === id ? { ...item, jsonValue } : item)))
  }

  const handleRemoveOverride = (id: string) => {
    setEditOverrides(prev => prev.filter(item => item.id !== id))
  }

  const handleRefreshCache = async () => {
    if (!ensureFreshEnvContext()) {
      return
    }
    try {
      await refreshMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.runtimeConfig.cacheRefreshed', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.runtimeConfig.cacheRefreshFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const renderValue = (entry: RuntimeConfigEntry) => {
    const value = entry.resolvedValue
    if (entry.key === 'PLATFORM_CONFIG_PREHEAT_CHAINS' && typeof value === 'string') {
      const codes = value.split(',').map(item => item.trim()).filter(Boolean)
      if (codes.length === 0) {
        return <Text color="gray.500">-</Text>
      }
      return (
        <HStack wrap="wrap">
          {codes.map(code => (
            <Badge key={code} colorScheme="blue">
              {chainNameByCode.get(code) ?? code}
            </Badge>
          ))}
        </HStack>
      )
    }

    if (entry.key === 'GAS_THRESHOLD_DEFAULTS' && typeof value === 'string') {
      try {
        const parsed = JSON.parse(value) as Record<string, unknown>
        const mainnetCount = parsed?.mainnet && typeof parsed.mainnet === 'object'
          ? Object.keys(parsed.mainnet as Record<string, unknown>).length
          : 0
        const testnetCount = parsed?.testnet && typeof parsed.testnet === 'object'
          ? Object.keys(parsed.testnet as Record<string, unknown>).length
          : 0
        return (
          <Text>
            {t('systemConfig.runtimeConfig.gasThresholdSummary', {
              defaultValue: 'Mainnet {{mainnet}} · Testnet {{testnet}}',
              mainnet: mainnetCount,
              testnet: testnetCount,
            })}
          </Text>
        )
      } catch {
        return <Code>{truncateText(value)}</Code>
      }
    }

    if (typeof value === 'boolean') {
      return (
        <Badge colorScheme={value ? 'green' : 'gray'}>
          {value ? t('common.enabled', { defaultValue: 'Enabled' }) : t('common.disabled', { defaultValue: 'Disabled' })}
        </Badge>
      )
    }
    if (typeof value === 'number') {
      return <Text>{value}</Text>
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
        || (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          const parsed = JSON.parse(value) as unknown
          if (parsed && typeof parsed === 'object') {
            const count = Array.isArray(parsed)
              ? parsed.length
              : Object.keys(parsed as Record<string, unknown>).length
            return (
              <Text color="gray.500">
                {t('systemConfig.runtimeConfig.jsonSummary', {
                  defaultValue: 'JSON ({{count}} items)',
                  count,
                })}
              </Text>
            )
          }
        } catch {
          return <Code>{truncateText(value)}</Code>
        }
      }
      return <Text>{truncateText(value)}</Text>
    }
    if (value && typeof value === 'object') {
      const json = JSON.stringify(value)
      return <Code>{truncateText(json)}</Code>
    }
    return <Text color="gray.500">-</Text>
  }

  const renderDefaultValue = (entry: RuntimeConfigEntry) => {
    const value = entry.defaultValue
    if (typeof value === 'boolean') {
      return (
        <Badge colorScheme={value ? 'green' : 'gray'}>
          {value ? t('common.enabled', { defaultValue: 'Enabled' }) : t('common.disabled', { defaultValue: 'Disabled' })}
        </Badge>
      )
    }
    if (typeof value === 'number') {
      return <Text>{value}</Text>
    }
    if (typeof value === 'string') {
      return <Text>{truncateText(value)}</Text>
    }
    if (value && typeof value === 'object') {
      return <Code>{truncateText(JSON.stringify(value))}</Code>
    }
    return <Text color="gray.500">-</Text>
  }

  const renderOverrides = (entry: RuntimeConfigEntry) => {
    const overrideEntries = Object.entries(entry.overrides)
    if (overrideEntries.length === 0) {
      return null
    }
    return (
      <HStack wrap="wrap" spacing={1} mt={1}>
        <Text fontSize="xs" color="gray.500">
          {t('systemConfig.runtimeConfig.overrides', { defaultValue: 'Overrides' })}:
        </Text>
        {overrideEntries.map(([env, value]) => (
          <Badge key={env} colorScheme="purple" fontSize="xs">
            {env}: {formatOverrideValue(value)}
          </Badge>
        ))}
      </HStack>
    )
  }

  return (
    <Card bg={props.cardBg}>
      <CardHeader py={2}>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between" align="center" wrap="wrap" spacing={3}>
            <HStack spacing={3} align="center" wrap="wrap">
              <Heading size="sm">
                {t('systemConfig.tabs.runtimeConfig', { defaultValue: 'runtime config' })}
              </Heading>
              <Text fontSize="xs" color="gray.500">
                {infoText}
              </Text>
              <HStack spacing={1}>
                <Badge colorScheme="blue" fontSize="xs">
                  {t('systemConfig.runtimeConfig.network', {
                    defaultValue: 'Network',
                  })}: {currentEnv}
                </Badge>
                <Badge colorScheme="purple" fontSize="xs">
                  {t('systemConfig.runtimeConfig.environment', {
                    defaultValue: 'Environment',
                  })}: {environmentKey}
                </Badge>
                <Badge colorScheme="teal" fontSize="xs">
                  {t('systemConfig.runtimeConfig.visibleEntries', {
                    defaultValue: 'Visible {{count}}',
                    count: entries.length,
                  })}
                </Badge>
                {sensitiveEntryCount > 0 && (
                  <Badge colorScheme="orange" fontSize="xs">
                    {t('systemConfig.runtimeConfig.hiddenSensitiveEntries', {
                      defaultValue: 'Hidden sensitive {{count}}',
                      count: sensitiveEntryCount,
                    })}
                  </Badge>
                )}
              </HStack>
            </HStack>
            <HStack spacing={2}>
              <RefreshIconButton
                label={t('common.refresh')}
                size="xs"
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                size="xs"
                onClick={handleRefreshCache}
                isLoading={refreshMutation.isPending}
              />
            </HStack>
          </HStack>

          <HStack justify="space-between" wrap="wrap" spacing={2}>
            <ButtonGroup isAttached size="xs" variant="outline">
              <Button
                onClick={() => setViewMode('table')}
                isActive={viewMode === 'table'}
              >
                {t('systemConfig.runtimeConfig.views.table', { defaultValue: 'Table' })}
              </Button>
              <Button
                onClick={() => setViewMode('modules')}
                isActive={viewMode === 'modules'}
              >
                {t('systemConfig.runtimeConfig.views.modules', { defaultValue: 'Modules' })}
              </Button>
            </ButtonGroup>
            <FormControl maxW={{ base: '100%', md: '240px' }}>
            <FormLabel fontSize="xs" mb={1}>{t('common.search')}</FormLabel>
            <Input
              size="xs"
              maxW="240px"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder={t('systemConfig.runtimeConfig.searchPlaceholder', {
                defaultValue: 'Search keys or labels',
              })}
            />
            </FormControl>
          </HStack>
        </VStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack align="stretch" spacing={3}>
          {filteredEntries.length === 0 ? (
            <Text color="gray.500">
              {t('systemConfig.runtimeConfig.empty', {
                defaultValue: 'No config entries found',
              })}
            </Text>
          ) : (
            <>
              {viewMode === 'table' ? (
                <VStack align="stretch" spacing={2}>
                  {filteredEntries.map(entry => (
                    <Box
                      key={entry.key}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                    >
                      <HStack justify="space-between" align="start">
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold">{entry.label}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {entry.key}
                          </Text>
                          {getEntryDescription(entry.key) && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {getEntryDescription(entry.key)}
                            </Text>
                          )}
                        </Box>
                        <Button
                          size="xs"
                          variant="ghost"
                          leftIcon={<FiEdit />}
                          isDisabled={isDataHydrating}
                          onClick={() => setEditingEntry(entry)}
                        >
                          {t('common.edit')}
                        </Button>
                      </HStack>
                      <HStack spacing={1} mt={1} wrap="wrap">
                        <Badge colorScheme="gray" fontSize="xs">{entry.moduleLabel}</Badge>
                        {entry.overrides[environmentKey] !== undefined && (
                          <Badge colorScheme="purple" fontSize="xs">
                            {t('systemConfig.runtimeConfig.badges.override', {
                              defaultValue: 'Override',
                            })}
                          </Badge>
                        )}
                        <Badge colorScheme="teal" fontSize="xs">
                          {t(`systemConfig.runtimeConfig.types.${entry.valueKind}`, {
                            defaultValue: entry.valueKind,
                          })}
                        </Badge>
                        <Text fontSize="xs" color="gray.500" ml={1}>
                          {t('systemConfig.runtimeConfig.columns.value', {
                            defaultValue: 'Current value',
                          })}:
                        </Text>
                        {renderValue(entry)}
                        <Text fontSize="xs" color="gray.500">
                          {t('systemConfig.runtimeConfig.columns.default', {
                            defaultValue: 'Default',
                          })}:
                        </Text>
                        {renderDefaultValue(entry)}
                      </HStack>
                      {renderOverrides(entry)}
                    </Box>
                  ))}
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  {sortedModuleIds.map(moduleId => {
                    const moduleEntries = groupedEntries.get(moduleId) ?? []
                    return (
                      <Box key={moduleId} borderWidth="1px" borderRadius="md" p={2}>
                        <Heading size="xs" mb={2}>{moduleLabel(moduleId)}</Heading>
                        <VStack align="stretch" spacing={2}>
                          {moduleEntries.map(entry => (
                            <Box key={entry.key}>
                              <HStack justify="space-between" align="start">
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold">{entry.label}</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {entry.key}
                                  </Text>
                                </Box>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<FiEdit />}
                                  isDisabled={isDataHydrating}
                                  onClick={() => setEditingEntry(entry)}
                                >
                                  {t('common.edit')}
                                </Button>
                              </HStack>
                              <HStack spacing={1} mt={1} wrap="wrap">
                                {entry.overrides[environmentKey] !== undefined && (
                                  <Badge colorScheme="purple" fontSize="xs">
                                    {t('systemConfig.runtimeConfig.badges.override', {
                                      defaultValue: 'Override',
                                    })}
                                  </Badge>
                                )}
                                <Badge colorScheme="teal" fontSize="xs">
                                  {t(`systemConfig.runtimeConfig.types.${entry.valueKind}`, {
                                    defaultValue: entry.valueKind,
                                  })}
                                </Badge>
                                <Text fontSize="xs" color="gray.500" ml={1}>
                                  {t('systemConfig.runtimeConfig.columns.value', {
                                    defaultValue: 'Current value',
                                  })}:
                                </Text>
                                {renderValue(entry)}
                                <Text fontSize="xs" color="gray.500">
                                  {t('systemConfig.runtimeConfig.columns.default', {
                                    defaultValue: 'Default',
                                  })}:
                                </Text>
                                {renderDefaultValue(entry)}
                              </HStack>
                              {renderOverrides(entry)}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )
                  })}
                </SimpleGrid>
              )}
            </>
          )}

          <Box>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setShowAdvanced(prev => !prev)}
            >
              {showAdvanced
                ? t('systemConfig.runtimeConfig.hideAdvanced', { defaultValue: 'Hide Advanced JSON' })
                : t('systemConfig.runtimeConfig.showAdvanced', { defaultValue: 'Show Advanced JSON' })}
            </Button>
            {showAdvanced && (
              <VStack align="stretch" spacing={2} mt={2}>
                {sensitiveEntryCount > 0 && (
                  <Text fontSize="xs" color="orange.500">
                    {t('systemConfig.runtimeConfig.advancedReadOnlyNotice', {
                      defaultValue:
                        'Advanced JSON only shows non-sensitive runtime keys. Secrets remain in server-side bindings or env.',
                    })}
                  </Text>
                )}
                <Textarea
                  minH="300px"
                  fontFamily="mono"
                  value={editorValue}
                  onChange={event => setEditorValue(event.target.value)}
                  spellCheck={false}
                />
                <HStack justify="flex-end">
                  <Button
                    size="xs"
                    colorScheme="admin"
                    leftIcon={<FiSave />}
                    onClick={handleSave}
                    isDisabled={isDataHydrating}
                    isLoading={updateMutation.isPending}
                  >
                    {t('common.save')}
                  </Button>
                </HStack>
              </VStack>
            )}
          </Box>
        </VStack>
      </CardBody>

      <Modal
        isOpen={Boolean(editingEntry)}
        onClose={() => setEditingEntry(null)}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent className="system-config-dialog" maxH="82vh" overflow="hidden">
          <ModalHeader py={3}>
            {t('systemConfig.runtimeConfig.editTitle', {
              defaultValue: 'Edit Runtime Config',
            })}
          </ModalHeader>
          <ModalCloseButton />
      <ModalBody overflowY="auto" py={2}>
        {editingEntry && (
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontWeight="semibold">{editingEntry.label}</Text>
              <Text fontSize="xs" color="gray.500">
                {editingEntry.key}
              </Text>
            </Box>
            <Box>
              <HStack justify="space-between" align="center" mb={2}>
                <Text fontSize="xs" color="gray.500">
                  {t('systemConfig.runtimeConfig.overrides', { defaultValue: 'Overrides' })}
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<FiPlus />}
                  onClick={handleAddOverride}
                >
                  {t('systemConfig.runtimeConfig.addOverride', { defaultValue: 'Add override' })}
                </Button>
              </HStack>
              {editOverrides.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  {t('systemConfig.runtimeConfig.overridesEmpty', { defaultValue: 'No overrides' })}
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {editOverrides.map((override) => (
                    <Box key={override.id} borderWidth="1px" borderRadius="md" p={2}>
                      <HStack spacing={2} align="center">
                        <Input
                          size="sm"
                          flex="1"
                          minW={0}
                          value={override.env}
                          onChange={event => handleUpdateOverrideEnv(override.id, event.target.value)}
                          placeholder={t('systemConfig.runtimeConfig.overrideEnv', {
                            defaultValue: 'Environment',
                          })}
                        />
                        {override.env.trim().toLowerCase() === environmentKey && (
                          <Badge colorScheme="blue" flexShrink={0}>
                            {t('systemConfig.runtimeConfig.currentEnv', { defaultValue: 'current' })}
                          </Badge>
                        )}
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={t('common.delete')}
                          icon={<FiTrash2 />}
                          onClick={() => handleRemoveOverride(override.id)}
                        />
                      </HStack>
                      <Box mt={2} minW={0}>
                        {editKind === 'boolean' ? (
                          <DropdownSelect
                            value={override.value}
                            options={booleanSelectOptions}
                            onChange={value => handleUpdateOverrideValue(override.id, value)}
                            placeholder={t('systemConfig.runtimeConfig.overrideValue', {
                              defaultValue: 'Override value',
                            })}
                            ariaLabel={t('systemConfig.runtimeConfig.overrideValue', {
                              defaultValue: 'Override value',
                            })}
                            size="xs"
                          />
                        ) : editKind === 'object' || editKind === 'json-string' ? (
                          <RuntimeConfigJsonEditor
                            value={override.jsonValue ?? {}}
                            onChange={value => handleUpdateOverrideJsonValue(override.id, value)}
                            editor={getRuntimeJsonDescriptor(editingEntry.key).editor}
                            readonlyPaths={getRuntimeJsonDescriptor(editingEntry.key).readonlyPaths}
                          />
                        ) : (
                          <Input
                            size="sm"
                            type={editKind === 'number' ? 'number' : 'text'}
                            value={override.value}
                            onChange={event =>
                              handleUpdateOverrideValue(override.id, event.target.value)
                            }
                            placeholder={t('systemConfig.runtimeConfig.overrideValue', {
                              defaultValue: 'Override value',
                            })}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
            <FormControl>
              <FormLabel>
                {t('systemConfig.runtimeConfig.columns.default', {
                  defaultValue: 'Default',
                })}
              </FormLabel>
                  {editKind === 'boolean' ? (
                    <DropdownSelect
                      value={editDefaultValue}
                      options={booleanSelectOptions}
                      onChange={setEditDefaultValue}
                      ariaLabel={t('systemConfig.runtimeConfig.columns.default', {
                        defaultValue: 'Default',
                      })}
                      size="sm"
                    />
                  ) : editKind === 'object' || editKind === 'json-string' ? (
                    <RuntimeConfigJsonEditor
                      value={editJsonDefaultValue ?? {}}
                      onChange={setEditJsonDefaultValue}
                      editor={getRuntimeJsonDescriptor(editingEntry.key).editor}
                      readonlyPaths={getRuntimeJsonDescriptor(editingEntry.key).readonlyPaths}
                    />
                  ) : (
                    <Input
                      type={editKind === 'number' ? 'number' : 'text'}
                      value={editDefaultValue}
                      onChange={event => setEditDefaultValue(event.target.value)}
                    />
                  )}
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter py={2}>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={() => setEditingEntry(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                leftIcon={<FiSave />}
                onClick={handleSaveEntry}
                isDisabled={isDataHydrating}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  )
}
